import { FC, FocusEvent, ReactNode, useMemo, useState } from 'react';
import { Button, Flex, Typography } from 'antd';
import {
    TbAdjustmentsHorizontal,
    TbBuilding,
    TbBuildingBank,
    TbBuildingFactory2,
    TbCashBanknote,
    TbChartBar,
    TbChartDots2,
    TbChartLine,
    TbFileDescription,
    TbMapPin,
    TbScale,
} from 'react-icons/tb';

import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableProvidedDraggableProps,
    DraggableProvidedDragHandleProps,
    DraggableStateSnapshot,
    Droppable,
    DropResult,
} from '@/shared/components/drag-and-drop';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import {
    FinancialOverviewLayoutPreferences,
    FinancialOverviewLayoutSection,
    FinancialOverviewLayoutSectionKey,
    FinancialOverviewVerticalLayoutProps,
} from '../../types';
import { FinancialOverviewLayoutCustomizeModal } from '../FinancialOverviewLayoutCustomizeModal';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';
import { FinancialOverviewSectionDragProvider } from '../FinancialOverviewSectionDragContext';
import { useFinancialOverviewSectionDrag } from '../FinancialOverviewSectionDragContext/context';
import { FinancialOverviewSectionDragHandle } from '../FinancialOverviewSectionDragHandle';
import { reorderFinancialOverviewVisibleSections } from './order';

const draggableIdFor = (key: FinancialOverviewLayoutSectionKey) => `financial-overview-live:${key}`;
const FINANCIAL_OVERVIEW_SECTION_GAP = '1rem';

const FinancialOverviewIdentityActions: FC<{ children: ReactNode }> = ({ children }) => {
    const dragControl = useFinancialOverviewSectionDrag();

    return (
        <Flex
            align='center'
            gap='0.625rem'
            onMouseEnter={() => dragControl?.onHoverChange?.(true)}
            onMouseLeave={() => dragControl?.onHoverChange?.(false)}
        >
            {dragControl ? <FinancialOverviewSectionDragHandle control={dragControl} /> : null}
            {children}
        </Flex>
    );
};

interface FinancialOverviewDraggableSectionProps {
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    draggableProps: DraggableProvidedDraggableProps;
    innerRef: DraggableProvided['innerRef'];
    isDragging: DraggableStateSnapshot['isDragging'];
    section: FinancialOverviewLayoutSection;
}

const FinancialOverviewDraggableSection: FC<FinancialOverviewDraggableSectionProps> = ({
    dragHandleProps,
    draggableProps,
    innerRef,
    isDragging,
    section,
}) => {
    const [focused, setFocused] = useState(false);
    const [headerHovered, setHeaderHovered] = useState(false);
    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocused(false);
        }
    };

    return (
        <div
            ref={innerRef}
            {...draggableProps}
            data-financial-overview-section={section.key}
            onBlurCapture={handleBlur}
            onFocusCapture={() => setFocused(true)}
            style={{
                marginBottom: FINANCIAL_OVERVIEW_SECTION_GAP,
                ...draggableProps.style,
            }}
        >
            <FinancialOverviewSectionDragProvider
                value={{
                    dragHandleProps,
                    isDragging,
                    label: `Reorder ${section.label}`,
                    onHoverChange: setHeaderHovered,
                    visible: focused || headerHovered || isDragging,
                }}
            >
                {section.node}
            </FinancialOverviewSectionDragProvider>
        </div>
    );
};

const getOverviewPreferences = (
    preferences: unknown,
    preferenceKey: string,
): FinancialOverviewLayoutPreferences | undefined => {
    const value = preferences as
        | {
              financialOverview?: {
                  layouts?: Record<string, FinancialOverviewLayoutPreferences>;
              };
          }
        | null
        | undefined;

    return value?.financialOverview?.layouts?.[preferenceKey];
};

const mergeSectionOrder = (
    savedOrder: FinancialOverviewLayoutSectionKey[],
    availableKeys: FinancialOverviewLayoutSectionKey[],
) => {
    const order = [...savedOrder];

    availableKeys.forEach((key, canonicalIndex) => {
        if (order.includes(key)) {
            return;
        }

        const precedingKeys = new Set(availableKeys.slice(0, canonicalIndex));
        const insertionIndex = order.reduce(
            (lastIndex, orderedKey, index) => (precedingKeys.has(orderedKey) ? index : lastIndex),
            -1,
        );

        order.splice(insertionIndex + 1, 0, key);
    });

    return order;
};

export const FinancialOverviewVerticalLayout: FC<FinancialOverviewVerticalLayoutProps> = ({
    aboutLinks,
    description,
    entityLabel = 'Company',
    holdings,
    holdingsSectionDescription = 'Largest reported institutional positions and ownership context.',
    holdingsSectionLabel = 'Institutional Holdings',
    identity,
    loading,
    preferenceKey,
    sections,
    style,
    supplementarySections = [],
}) => {
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const { preferences, setPreference } = useUserPreferences();
    const entityNoun = entityLabel.toLocaleLowerCase();
    const entityArticle = /^[aeiou]/i.test(entityLabel) ? 'An' : 'A';
    const profileContext = entityNoun === 'fund' ? 'investment mandate' : 'business model';
    const availableSections = useMemo<FinancialOverviewLayoutSection[]>(
        () => [
            {
                description: `${entityLabel} profile, branding, identifiers, and operating metadata.`,
                icon: <TbBuilding />,
                key: 'identity',
                label: 'Identity',
                locked: true,
                node: (
                    <section>
                        {identity(
                            <FinancialOverviewIdentityActions>
                                <Button
                                    icon={<TbAdjustmentsHorizontal style={{ verticalAlign: '-0.125rem' }} />}
                                    onClick={() => setCustomizeOpen(true)}
                                    size='small'
                                >
                                    Customize
                                </Button>
                            </FinancialOverviewIdentityActions>,
                        )}
                    </section>
                ),
            },
            {
                description: `Profile and narrative for this ${entityNoun}.`,
                icon: <TbFileDescription />,
                key: 'about',
                label: `About the ${entityLabel}`,
                node: (
                    <FinancialOverviewPanel
                        description={`Review the ${entityNoun} narrative, ${profileContext}, and primary external profiles.`}
                        icon={<TbFileDescription style={{ verticalAlign: '-0.125rem' }} />}
                        title={`About the ${entityLabel}`}
                    >
                        {description && !loading ? (
                            <>
                                <Typography.Paragraph
                                    ellipsis={{ rows: 5, expandable: true, symbol: 'See more' }}
                                    style={{ marginBottom: 0, width: '100%' }}
                                >
                                    {description}
                                </Typography.Paragraph>
                                {aboutLinks ? <div style={{ marginTop: '0.75rem' }}>{aboutLinks}</div> : null}
                            </>
                        ) : loading ? (
                            <BaseSkeleton paragraph={{ rows: 5 }} />
                        ) : (
                            <EmptySection
                                description={`${entityArticle} ${entityNoun} description has not been provided for this profile.`}
                                height='12rem'
                                icon={<TbFileDescription size='1.25rem' />}
                                label={`${entityLabel} Description Unavailable`}
                            />
                        )}
                        {!description && !loading && aboutLinks ? (
                            <div style={{ marginTop: '0.75rem' }}>{aboutLinks}</div>
                        ) : null}
                    </FinancialOverviewPanel>
                ),
            },
            ...(sections.marketPerformance
                ? [
                      {
                          description: 'Historical price performance and comparative market series.',
                          icon: <TbChartLine />,
                          key: 'marketPerformance' as const,
                          label: 'Market Performance',
                          node: sections.marketPerformance,
                      },
                  ]
                : []),
            ...(sections.keyStatistics
                ? [
                      {
                          description: 'Valuation, capitalization, profitability, and operating statistics.',
                          icon: <TbChartBar />,
                          key: 'keyStatistics' as const,
                          label: 'Key Stats',
                          node: sections.keyStatistics,
                      },
                  ]
                : []),
            ...(sections.corporateDebt
                ? [
                      {
                          description:
                              'Debt balances, financing instruments, maturity context, and liquidity coverage.',
                          icon: <TbCashBanknote />,
                          key: 'corporateDebt' as const,
                          label: 'Corporate Debt',
                          node: sections.corporateDebt,
                      },
                  ]
                : []),
            ...(sections.capitalStructure
                ? [
                      {
                          description: 'Equity value, debt, cash, and enterprise value composition.',
                          icon: <TbScale />,
                          key: 'capitalStructure' as const,
                          label: 'Capital Structure',
                          node: sections.capitalStructure,
                      },
                  ]
                : []),
            ...(sections.sectorKpis
                ? [
                      {
                          description: 'Sector-specific operating indicators and peer benchmarks.',
                          icon: <TbChartDots2 />,
                          key: 'sectorKpis' as const,
                          label: 'Sector KPIs',
                          node: sections.sectorKpis,
                      },
                  ]
                : []),
            ...(sections.kpis
                ? [
                      {
                          description: 'Industry-specific efficiency, retention, and unit-economics indicators.',
                          icon: <TbBuildingFactory2 />,
                          key: 'kpis' as const,
                          label: 'Industry KPIs',
                          node: sections.kpis,
                      },
                  ]
                : []),
            ...(holdings
                ? [
                      {
                          description: holdingsSectionDescription,
                          icon: <TbBuildingBank />,
                          key: 'institutionalHoldings' as const,
                          label: holdingsSectionLabel,
                          node: holdings,
                      },
                  ]
                : []),
            ...(sections.location
                ? [
                      {
                          description: 'Headquarters and primary operating location.',
                          icon: <TbMapPin />,
                          key: 'location' as const,
                          label: 'Location',
                          node: sections.location,
                      },
                  ]
                : []),
            ...supplementarySections,
        ],
        [
            aboutLinks,
            description,
            entityArticle,
            entityLabel,
            entityNoun,
            holdings,
            holdingsSectionDescription,
            holdingsSectionLabel,
            identity,
            loading,
            profileContext,
            sections,
            supplementarySections,
        ],
    );

    const identitySection = availableSections.find((section) => section.key === 'identity');
    const reorderableSections = availableSections.filter((section) => section.key !== 'identity');
    const savedPreferences = getOverviewPreferences(preferences, preferenceKey);
    const availableKeys = reorderableSections.map((section) => section.key);
    const availableKeySet = new Set(availableKeys);
    const savedOrder = (savedPreferences?.order ?? []).filter((key) => availableKeySet.has(key));
    const order = mergeSectionOrder(savedOrder, availableKeys);
    const defaultHidden = supplementarySections.map((section) => section.key);
    const hidden = (savedPreferences?.hidden ?? defaultHidden).filter(
        (key) => key !== 'identity' && availableKeySet.has(key),
    );
    const hiddenSet = new Set(hidden);
    const sectionByKey = new Map(reorderableSections.map((section) => [section.key, section] as const));
    const orderedVisibleSections = order
        .map((key) => sectionByKey.get(key))
        .filter(
            (section): section is FinancialOverviewLayoutSection =>
                section !== undefined && !hiddenSet.has(section.key),
        );
    const preferencePath = `financialOverview.layouts.${preferenceKey}`;

    const updateOrder = (nextOrder: FinancialOverviewLayoutSectionKey[]) => {
        setPreference(`${preferencePath}.order`, nextOrder);
    };

    const updateHidden = (nextHidden: FinancialOverviewLayoutSectionKey[]) => {
        setPreference(`${preferencePath}.hidden`, nextHidden);
    };

    const handleLiveDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        updateOrder(
            reorderFinancialOverviewVisibleSections(
                order,
                orderedVisibleSections.map((section) => section.key),
                result.source.index,
                result.destination.index,
            ),
        );
    };

    return (
        <div
            style={{
                boxSizing: 'border-box',
                maxWidth: '100%',
                overflowX: 'clip',
                paddingBottom: '1rem',
                width: '100%',
                ...style,
            }}
        >
            <div
                data-financial-overview-sections='true'
                style={{ display: 'grid', gap: FINANCIAL_OVERVIEW_SECTION_GAP }}
            >
                {identitySection ? (
                    <div data-financial-overview-fixed-section='identity'>{identitySection.node}</div>
                ) : null}
                <DragDropContext onDragEnd={handleLiveDragEnd}>
                    <Droppable droppableId={`financial-overview-live:${preferenceKey}`}>
                        {(provided) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                data-financial-overview-reorderable-sections='true'
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'grid',
                                    gap: 0,
                                    marginBottom: `-${FINANCIAL_OVERVIEW_SECTION_GAP}`,
                                    padding: '0 1rem',
                                    width: '100%',
                                }}
                            >
                                {orderedVisibleSections.map((section, index) => (
                                    <Draggable
                                        draggableId={draggableIdFor(section.key)}
                                        index={index}
                                        key={section.key}
                                    >
                                        {(provided, snapshot) => (
                                            <FinancialOverviewDraggableSection
                                                dragHandleProps={provided.dragHandleProps}
                                                draggableProps={provided.draggableProps}
                                                innerRef={provided.innerRef}
                                                isDragging={snapshot.isDragging}
                                                section={section}
                                            />
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
            {customizeOpen ? (
                <FinancialOverviewLayoutCustomizeModal
                    hidden={hidden}
                    onClose={() => setCustomizeOpen(false)}
                    onHiddenChange={updateHidden}
                    onOrderChange={updateOrder}
                    onReset={() =>
                        setPreference(preferencePath, {
                            hidden: defaultHidden,
                            order: availableKeys,
                        })
                    }
                    open={customizeOpen}
                    order={order}
                    sections={reorderableSections}
                />
            ) : null}
        </div>
    );
};
